import { useMutation, useQuery } from "@apollo/client";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { UPDATE_DENY_STATUS } from "../../../../queries/mutation/denystatus";
import { GET_DENY_STATUS } from "../../../../queries/user_queries";
import { DenyStatus } from "../../../../types/DenyStatus";
import Loading from "../../../Loading";
import DashCard from "../DashCard";

const DenyStatusUpdate = () => {
  const [denyStatus, setDenyStatus] = useState<DenyStatus | undefined>();
  const serverValuesRef = useRef<{
    numberOfDenies: number;
    numberOfUndenies: number;
  } | null>(null);

  // broadcasterName used to be sent from here, but the resolver never read
  // it - it's decided server-side now (see resolvers.ts's `broadcaster`).
  const { loading, error, data } = useQuery(GET_DENY_STATUS);

  const [
    updateDenyStatus,
    { data: updateData, error: addMutationError, loading: updateLoading },
  ] = useMutation(UPDATE_DENY_STATUS);

  useEffect(() => {
    if (data) {
      const status = data.getDenyStatus;
      setDenyStatus(status);
      // Store server values for potential revert
      serverValuesRef.current = {
        numberOfDenies: status.numberOfDenies,
        numberOfUndenies: status.numberOfUndenies,
      };
    }
  }, [data]);

  if (denyStatus === undefined) {
    return <Loading />;
  }

  const { isDenied, deniedBy, undeniedBy, numberOfDenies, numberOfUndenies } =
    denyStatus;

  const updateStatus = (isDeny: boolean, e: ChangeEvent<HTMLInputElement>) => {
    let number = parseInt(e.target.value);
    if (isDeny) {
      setDenyStatus({ ...denyStatus, numberOfDenies: number });
    } else {
      setDenyStatus({ ...denyStatus, numberOfUndenies: number });
    }
  };

  const submit = async () => {
    try {
      const { data } = await updateDenyStatus({
        variables: {
          updateDenyStatusInput: {
            numberOfDenies: denyStatus.numberOfDenies,
            numberOfUndenies: denyStatus.numberOfUndenies,
          },
        },
      });
      // Update server values ref on success
      if (data?.updateDenyStatus) {
        serverValuesRef.current = {
          numberOfDenies: data.updateDenyStatus.numberOfDenies,
          numberOfUndenies: data.updateDenyStatus.numberOfUndenies,
        };
      }
      toast.success("Deny status updated successfully!");
    } catch (error: any) {
      // Revert to last known server values on failure
      if (serverValuesRef.current) {
        setDenyStatus({
          ...denyStatus,
          numberOfDenies: serverValuesRef.current.numberOfDenies,
          numberOfUndenies: serverValuesRef.current.numberOfUndenies,
        });
      }
      toast.error(`Failed to update deny status: ${error.message}`);
    }
  };

  return (
    <DashCard
      title="Deny status"
      aside={
        <button
          className="btn btn-sm btn-success"
          onClick={submit}
          disabled={updateLoading}
        >
          Update
        </button>
      }
    >
      <div className="grid grid-cols-2 gap-2">
        <label className="form-control">
          <span className="label-text text-error text-xs">Denies</span>
          <input
            type="number"
            value={numberOfDenies}
            onChange={(e) => updateStatus(true, e)}
            className="input input-bordered input-sm w-full"
          />
        </label>
        <label className="form-control">
          <span className="label-text text-success text-xs">Undenies</span>
          <input
            type="number"
            value={numberOfUndenies}
            onChange={(e) => updateStatus(false, e)}
            className="input input-bordered input-sm w-full"
          />
        </label>
      </div>
    </DashCard>
  );
};

export default DenyStatusUpdate;
